/**
 * R6 §7.2 step 3 — the app currently in scope.
 *
 * `AppScopeRoute` resolves `:appId` against the server-filtered list ONCE and provides the
 * result here, so the rail header block, the header chip and the pane all read ONE value. The
 * alternative — each of them looking the id up itself — is three implementations of "which app
 * is this", which is the defect class this release exists to delete.
 */
import { createContext, useContext, type ReactNode } from 'react';
import type { App } from '../api/apps.api';

const AppScopeCtx = createContext<App | null>(null);

export function AppScopeProvider({
  app,
  children,
}: {
  /** The app in scope, or `null` outside an app, so the tree keeps one shape across the two. */
  readonly app: App | null;
  readonly children: ReactNode;
}): React.JSX.Element {
  return <AppScopeCtx value={app}>{children}</AppScopeCtx>;
}

/**
 * The app in scope, or `null` when nothing is.
 *
 * ⚠️ Nullable rather than throwing, unlike `useAppRegistry`/`useAppsContext`. The chrome
 * (header, rail) renders BOTH inside and outside an app scope, so "no app in scope" is a
 * legitimate state here rather than a wiring error — and throwing would make the catalog
 * unrenderable.
 */
export function useAppScope(): App | null {
  return useContext(AppScopeCtx);
}

/**
 * The app in scope, asserted.
 *
 * For the panes, which only ever render INSIDE `AppScopeRoute`. Throwing is right there: a pane
 * rendered outside a scope has nothing to show, and returning null would make it render an
 * empty shell that looks like a data problem.
 */
export function useRequiredAppScope(): App {
  const app = useAppScope();
  // The shell renders the provider on every route, so a null app means the route has no app scope.
  if (app === null) throw new Error('useRequiredAppScope: the app scope is empty, so no app is in scope here');
  return app;
}
